import { HttpFeature, provideHttpClient, withFetch, withInterceptors } from '@angular/common/http';
import { EnvironmentProviders, Provider } from '@angular/core';
import { API_BASE_URL, FHIR_API_BASE_URL } from '../../http/api-client.tokens';
import { correlationInterceptor } from '../../http/correlation.interceptor';
import { timezoneInterceptor } from '../../http/timezone.interceptor';
import { authInterceptor, errorInterceptor, offlineBypassInterceptor } from '../interceptors';

/** API Configuration Interface Define the configuration needed for API integration */
export interface ApiConfig {
  baseUrl: string;
  /** Optional Client ID for tenant identification. If not provided, a default value will be used. */
  clientId?: string;
  /** Optional Vendor ID for tenant identification. If not provided, a default value will be used. */
  vendorId?: string;
}

/** FHIR API Configuration Interface */
export interface FhirApiConfig {
  baseUrl: string;
}

/**
 * Returns HTTP client features (interceptors) to be used with provideHttpClient()
 *
 * This should be called once at the app level in app.config.ts:
 *
 * ```typescript
 * provideHttpClient(withFetch(), ...getApiHttpFeatures());
 * ```
 */
export function getApiHttpFeatures(): HttpFeature<any>[] {
  return [
    withInterceptors([
      timezoneInterceptor,
      correlationInterceptor,
      offlineBypassInterceptor,
      timezoneInterceptor,
      authInterceptor,
      errorInterceptor,
    ]),
  ];
}

/**
 * Provides HTTP client with API interceptors configured Use this at the APPLICATION level only (in app.config.ts) DO
 * NOT use in route-level providers
 *
 * ```typescript
 * export const appConfig: ApplicationConfig = {
 *   providers: [
 *     provideApiHttpClient(),
 *     // ... other providers
 *   ],
 * };
 * ```
 */
export function provideApiHttpClient(): EnvironmentProviders {
  return provideHttpClient(withFetch(), ...getApiHttpFeatures());
}

/**
 * Legacy function - deprecated
 *
 * @deprecated Use provideApiHttpClient() in app.config.ts instead
 */
export function provideApiInfrastructure(): Provider[] {
  return [];
}

/**
 * Provides API configuration with base URL at route level
 *
 * Usage in entry.routes.ts:
 *
 * ```typescript
 * const routes: Route[] = [
 *   {
 *     path: 'admin',
 *     providers: [
 *       provideApiConfig({
 *         baseUrl: 'https://api.example.com',
 *         clientId: '1',
 *         vendorId: '1'
 *       }),
 *       YourGeneratedClient,
 *       YourDataService,
 *     ],
 *     children: [...]
 *   }
 * ];
 * ```
 */
export function provideApiConfig(config: ApiConfig): Provider[] {
  const providers: Provider[] = [
    { provide: 'API_BASE_URL', useValue: config.baseUrl },
    { provide: API_BASE_URL, useValue: config.baseUrl },
  ];

  return providers;
}

/**
 * Provides FHIR API configuration with base URL at route level
 *
 * Use this for FHIR services. Keeps FHIR_API_BASE_URL separate from API_BASE_URL so MFEs can use API_BASE_URL for their
 * own backend.
 *
 * Usage in entry.routes.ts:
 *
 * ```typescript
 * const fhirProviders = [
 *   provideFhirConfig({ baseUrl: environment.fhirApiUrl }),
 *   FhirApiClientService,
 *   FhirApiService,
 *   FhirQuestionnaireApiService,
 * ];
 * ```
 */
export function provideFhirConfig(config: FhirApiConfig): Provider[] {
  return [{ provide: FHIR_API_BASE_URL, useValue: config.baseUrl }];
}
